/**
 * Export utilities for polls (CSV, PDF, JSON)
 * Format specs: /Docs/Export.md
 */

import type { UnifiedPoll } from "./pollStorageUnified";
import type { FormResponse, Vote } from "./pollStorage"; // Garder si utilisé
import { getFormResponses, getVotesByPollId } from "./pollStorage";
import { ErrorFactory } from "./error-handling";
import i18n from "@core/i18n";

/**
 * Escape CSV values (RFC 4180 compliant)
 */
function escapeCSV(value: string | number | undefined): string {
  if (value === undefined || value === null) return "";
  let str = String(value);
  // Anti-spoofing : retire les caractères de contrôle bidi Unicode
  // (RLO/LRO/PDF/LRI/RLI/FSI/PDI) qui peuvent masquer le contenu réel.
  str = str.replace(/[‪-‮⁦-⁩]/g, "");
  // Anti "CSV formula injection" (Excel/Sheets/LibreOffice, DDE) : préfixe
  // les leaders dangereux par une apostrophe pour neutraliser la formule.
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }
  // RFC 4180 : double les quotes internes, puis wrap si nécessaire.
  if (
    str.includes(",") ||
    str.includes("\n") ||
    str.includes("\r") ||
    str.includes('"') ||
    str.startsWith("'")
  ) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Generate CSV header with BOM for UTF-8 encoding
 */
function getCSVHeader(): string {
  return "﻿"; // UTF-8 BOM
}

/**
 * Format date to ISO 8601 (UTC)
 */
function formatTimestamp(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toISOString();
}

/**
 * Generate filename for export
 * Format: doodates_{type}_{slug}_{YYYYMMDD-HHmmss}.csv
 */
function generateFilename(
  poll: UnifiedPoll,
  extension: string = "csv",
): string {
  const now = new Date();
  const timestamp = now
    .toISOString()
    .replace(/[-:]/g, "")
    .replace("T", "-")
    .slice(0, 15); // YYYYMMDD-HHmmss
  const slug = poll.slug || "poll";
  const type = poll.type;
  return `doodates_${type}_${slug}_${timestamp}.${extension}`;
}

/**
 * Export DateUnifiedPoll to CSV
 */
export function dateUnifiedPollToCSV(
  poll: UnifiedPoll,
  preloadedVotes?: Vote[],
): string {
  if (poll.type !== "date") {
    throw ErrorFactory.validation(
      "UnifiedPoll type must be 'date'",
      i18n.t("exports.error.notDatePoll"),
    );
  }

  const lines: string[] = [];
  const votes = preloadedVotes ?? getVotesByPollId(poll.id);
  const datePollData = poll.poll_data_json || {};

  // Header with BOM
  lines.push(getCSVHeader());

  // Metadata header
  lines.push(
    [
      "poll_id",
      "poll_slug",
      "poll_title",
      "poll_description",
      "poll_settings",
      "poll_dates",
      "poll_timezone",
      "poll_created_at",
      "poll_updated_at",
    ]
      .map((header) => escapeCSV(header))
      .join(","),
  );

  // Metadata values
  lines.push(
    [
      poll.id,
      poll.slug,
      poll.title,
      poll.description || "",
      JSON.stringify(datePollData.settings || {}),
      JSON.stringify(datePollData.dates || []),
      datePollData.settings?.timezone || "",
      poll.created_at,
      poll.updated_at,
    ]
      .map((val: string | number) => escapeCSV(val))
      .join(","),
  );

  // Empty line separator
  lines.push("");

  // Data header
  lines.push(
    (
      [
        "respondent_display",
        "respondent_hash",
        "submitted_at",
        "date_option",
        "response",
        "respondent_email",
        "respondent_name",
      ] as Array<string>
    )
      .map((val: string) => escapeCSV(val))
      .join(","),
  );

  // Data rows
  const pollData = poll.poll_data_json || {};
  const selectedDates =
    pollData.settings?.selectedDates || pollData.dates || [];

  votes.forEach((vote: Vote) => {
    // Les votes stockés portent voter_* (ou respondent_* selon la source) ;
    // respondentName / respondentEmail restent lus pour les anciens appelants.
    const voteName = [
      vote.voter_name,
      vote.respondent_name,
      vote.respondentName,
    ].find((v) => typeof v === "string" && v) as string | undefined;
    const voteEmail = [
      vote.voter_email,
      vote.respondent_email,
      vote.respondentEmail,
    ].find((v) => typeof v === "string" && v) as string | undefined;
    const respondentDisplay =
      voteName || voteEmail || i18n.t("exports.anonymous");
    const respondentHash = vote.id || "unknown";
    const submittedAt = formatTimestamp(vote.created_at || new Date());
    const respondentEmail = voteEmail || "";
    const respondentName = voteName || "";

    // Extract vote data for each date option
    const voteData = (vote as any).vote_data || (vote as any).selections || {};

    selectedDates.forEach((dateOption: any, index: number) => {
      const dateStr =
        typeof dateOption === "string"
          ? dateOption
          : dateOption.label || dateOption.title || String(dateOption);

      const optionId = (index + 1).toString();
      const response = voteData[optionId] || "no_response";

      lines.push(
        (
          [
            respondentDisplay,
            respondentHash,
            submittedAt,
            escapeCSV(dateStr),
            response,
            respondentEmail,
            respondentName,
          ] as Array<string | number>
        )
          .map((val: string | number) => escapeCSV(val))
          .join(","),
      );
    });
  });

  return lines.join("\n");
}

/**
 * Export FormUnifiedPoll to CSV (long format)
 * Structure: 1 line per (respondent, question)
 */
export function formUnifiedPollToCSV(
  poll: UnifiedPoll,
  preloadedResponses?: import("./pollStorage").FormResponse[],
): string {
  if (poll.type !== "form") {
    throw ErrorFactory.validation(
      "UnifiedPoll type must be 'form'",
      i18n.t("exports.error.notFormPoll"),
    );
  }

  const lines: string[] = [];

  // Header with BOM
  lines.push(getCSVHeader());

  // Metadata header
  lines.push(
    (
      [
        "poll_id",
        "poll_slug",
        "poll_title",
        "type",
        "created_at",
        "exported_at",
      ] as Array<string>
    )
      .map((val: string) => escapeCSV(val))
      .join(","),
  );

  // Metadata values
  lines.push(
    (
      [
        poll.id,
        poll.slug,
        poll.title,
        poll.type,
        formatTimestamp(poll.created_at),
        formatTimestamp(new Date()),
      ] as Array<string | number | undefined>
    )
      .map((val: string | number | undefined) => escapeCSV(val))
      .join(","),
  );

  // Empty line separator
  lines.push("");

  // Data header
  lines.push(
    (
      [
        "respondent_display",
        "respondent_hash",
        "submitted_at",
        "source",
        "question_id",
        "question_title",
        "question_type",
        "value",
        "selected_count",
      ] as Array<string>
    )
      .map((val: string) => escapeCSV(val))
      .join(","),
  );

  // Data rows
  const responses = preloadedResponses ?? getFormResponses(poll.id);

  responses.forEach((response: import("./pollStorage").FormResponse) => {
    const respondentDisplay =
      response.respondentName || i18n.t("exports.anonymous");
    const respondentHash = response.id || "unknown";
    const submittedAt = formatTimestamp(response.created_at || new Date());
    const source = "link"; // MVP: default to "link" (can be extended later)

    response.items.forEach((item: { questionId: string; value: unknown }) => {
      const question = poll.poll_data_json?.questions?.find(
        (q: import("./pollStorage").FormQuestionShape) =>
          q.id === item.questionId,
      );
      const questionTitle =
        question?.title || i18n.t("exports.unknownQuestion");
      const questionType = question?.type || "unknown";

      let value: string;
      let selectedCount: number | string = "";

      if (Array.isArray(item.value)) {
        // Multiple choice: join with pipe
        value = item.value.join(" | ");
        selectedCount = item.value.length;
      } else if (typeof item.value === "object" && item.value !== null) {
        // Matrix: format as "Row1: Col1 | Row2: Col2"
        const matrixVal = item.value as Record<string, string | string[]>;
        const matrixRows = question.matrixRows || [];
        const matrixCols = question.matrixColumns || [];

        const formatted = Object.entries(matrixVal).map(
          ([rowId, colValue]: [string, string | string[]]) => {
            const row = matrixRows.find(
              (r: { id: string; label: string }) => r.id === rowId,
            );
            const rowLabel = row?.label || rowId;

            const colIds = Array.isArray(colValue) ? colValue : [colValue];
            const colLabels = colIds.map((cid: string) => {
              const col = matrixCols.find(
                (c: { id: string; label: string }) => c.id === cid,
              );
              return col?.label || cid;
            });

            return `${rowLabel}: ${colLabels.join(", ")}`;
          },
        );

        value = formatted.join(" | ");
        selectedCount = Object.keys(matrixVal).length;
      } else {
        // Single choice or text
        value = String(item.value);
        selectedCount = 1;
      }

      lines.push(
        (
          [
            respondentDisplay,
            respondentHash,
            submittedAt,
            source,
            item.questionId,
            questionTitle,
            questionType,
            value,
            selectedCount,
          ] as Array<string | number>
        )
          .map((val: string | number) => escapeCSV(val))
          .join(","),
      );
    });
  });

  return lines.join("\n");
}

/**
 * Trigger CSV download in browser
 */
export function downloadCSV(content: string, poll: UnifiedPoll): void {
  const filename = generateFilename(poll, "csv");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export DateUnifiedPoll to CSV and trigger download
 */
export function exportDateUnifiedPollToCSV(
  poll: UnifiedPoll,
  preloadedVotes?: Vote[],
): void {
  if (poll.type !== "date") {
    throw ErrorFactory.validation(
      "This function only supports DateUnifiedPolls",
      i18n.t("exports.error.onlyDatePolls"),
    );
  }

  const csv = dateUnifiedPollToCSV(poll, preloadedVotes);
  downloadCSV(csv, poll);
}

/**
 * Export FormUnifiedPoll to CSV and trigger download
 */
export function exportFormUnifiedPollToCSV(
  poll: UnifiedPoll,
  preloadedResponses?: import("./pollStorage").FormResponse[],
): void {
  if (poll.type !== "form") {
    throw ErrorFactory.validation(
      "This function only supports FormUnifiedPoll",
      i18n.t("exports.error.onlyForms"),
    );
  }

  const csv = formUnifiedPollToCSV(poll, preloadedResponses);
  downloadCSV(csv, poll);
}

/**
 * Check if poll has data to export
 */
export function hasExportableData(poll: UnifiedPoll): boolean {
  if (poll.type === "form") {
    const responses = getFormResponses(poll.id);
    return responses.length > 0;
  }
  if (poll.type === "date") {
    // For date polls, check if there are any votes
    const votes = getVotesByPollId(poll.id);
    return votes && votes.length > 0;
  }
  return false; // Other types not supported yet
}

/**
 * Validate export permissions (MVP: basic check)
 */
export function canExport(poll: UnifiedPoll, userId?: string): boolean {
  // MVP: Allow export if poll has data
  // TODO: Add proper ownership check when auth is implemented
  return hasExportableData(poll);
}

/**
 * Generate HTML template for PDF export
 * User can print to PDF via browser (Ctrl+P)
 */
function formUnifiedPollToHTML(
  poll: UnifiedPoll,
  preloadedResponses?: import("./pollStorage").FormResponse[],
): string {
  if (poll.type !== "form") {
    throw ErrorFactory.validation(
      "UnifiedPoll type must be 'form'",
      i18n.t("exports.error.notFormPoll"),
    );
  }

  const responses = preloadedResponses ?? getFormResponses(poll.id);
  const exportDate = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  // Build questions summary
  let questionsHTML = "";
  poll.poll_data_json?.questions?.forEach(
    (question: import("./pollStorage").FormQuestionShape, idx: number) => {
      questionsHTML += `
      <div class="question-block">
        <h3 class="question-title">${i18n.t("exports.question")} ${idx + 1}: ${escapeHTML(question.title)}</h3>
        <p class="question-meta">${i18n.t("exports.type")}: ${question.type} ${question.required ? `• ${i18n.t("exports.required")}` : ""}</p>
    `;

      // Count responses per question
      const questionResponses = responses.flatMap(
        (r: import("./pollStorage").FormResponse) =>
          r.items.filter(
            (item: { questionId: string }) => item.questionId === question.id,
          ),
      );

      if (question.type === "text") {
        questionsHTML += `<div class="responses">`;
        questionResponses.forEach((item: { value: unknown }, i: number) => {
          questionsHTML += `<p class="text-response"><strong>${i18n.t("exports.response")} ${i + 1}:</strong> ${escapeHTML(String(item.value))}</p>`;
        });
        questionsHTML += `</div>`;
      } else {
        // Count options for single/multiple choice
        const optionCounts: Record<string, number> = {};
        questionResponses.forEach((item: { value: unknown }) => {
          if (Array.isArray(item.value)) {
            (item.value as Array<string>).forEach((v: string) => {
              optionCounts[v] = (optionCounts[v] || 0) + 1;
            });
          } else {
            optionCounts[String(item.value)] =
              (optionCounts[String(item.value)] || 0) + 1;
          }
        });

        questionsHTML += `<table class="results-table">
        <thead>
          <tr>
            <th>${i18n.t("exports.option")}</th>
            <th>${i18n.t("exports.responses")}</th>
            <th>${i18n.t("exports.percentage")}</th>
          </tr>
        </thead>
        <tbody>`;

        Object.entries(optionCounts).forEach(
          ([option, count]: [string, number]) => {
            const percentage =
              responses.length > 0
                ? ((count / responses.length) * 100).toFixed(1)
                : "0.0";
            questionsHTML += `
          <tr>
            <td>${escapeHTML(option)}</td>
            <td>${count}</td>
            <td>${percentage}%</td>
          </tr>`;
          },
        );

        questionsHTML += `</tbody></table>`;
      }

      questionsHTML += `</div>`;
    },
  );

  return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHTML(poll.title)} - ${i18n.t("exports.pdfTitleSuffix")}</title>
  <style>
    @page {
      margin: 2cm;
      size: A4;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 1000px;
      margin: 0 auto;
      padding: 20px;
    }
    .header {
      border-bottom: 3px solid #2563eb;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    h1 {
      color: #1e40af;
      font-size: 28px;
      margin-bottom: 10px;
    }
    .metadata {
      display: flex;
      gap: 30px;
      flex-wrap: wrap;
      color: #666;
      font-size: 14px;
      margin-top: 15px;
    }
    .metadata-item {
      display: flex;
      align-items: center;
      gap: 5px;
    }
    .metadata-label {
      font-weight: 600;
    }
    .question-block {
      margin-bottom: 40px;
      page-break-inside: avoid;
    }
    .question-title {
      color: #1e40af;
      font-size: 18px;
      margin-bottom: 8px;
    }
    .question-meta {
      color: #666;
      font-size: 14px;
      margin-bottom: 15px;
    }
    .results-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
    }
    .results-table th {
      background: #f3f4f6;
      padding: 12px;
      text-align: left;
      font-weight: 600;
      border-bottom: 2px solid #e5e7eb;
    }
    .results-table td {
      padding: 10px 12px;
      border-bottom: 1px solid #e5e7eb;
    }
    .results-table tbody tr:hover {
      background: #f9fafb;
    }
    .text-response {
      padding: 10px;
      background: #f9fafb;
      border-left: 3px solid #2563eb;
      margin-bottom: 8px;
    }
    .responses {
      margin-top: 10px;
    }
    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      color: #666;
      font-size: 12px;
    }
    @media print {
      body {
        padding: 0;
      }
      .no-print {
        display: none;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${escapeHTML(poll.title)}</h1>
    <div class="metadata">
      <div class="metadata-item">
        <span class="metadata-label">${i18n.t("exports.type")}:</span>
        <span>${i18n.t("exports.questionnaire")}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">${i18n.t("exports.responses")}:</span>
        <span>${responses.length}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">${i18n.t("exports.questions")}:</span>
        <span>${poll.poll_data_json?.questions?.length || 0}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">${i18n.t("exports.exportedOn")}:</span>
        <span>${exportDate}</span>
      </div>
    </div>
  </div>

  ${questionsHTML}

  <div class="footer">
    <p>${i18n.t("exports.generatedBy")} • ${exportDate}</p>
    <p>ID: ${poll.id} • Slug: ${poll.slug}</p>
  </div>

  <script>
    // Auto-trigger print dialog
    window.onload = () => {
      setTimeout(() => window.print(), 500);
    };
  </script>
</body>
</html>`;
}

/**
 * Escape HTML special characters
 */
function escapeHTML(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Export FormUnifiedPoll to PDF (via print dialog)
 */
export function exportFormUnifiedPollToPDF(
  poll: UnifiedPoll,
  preloadedResponses?: import("./pollStorage").FormResponse[],
): void {
  if (poll.type !== "form") {
    throw ErrorFactory.validation(
      "This function only supports FormUnifiedPoll",
      i18n.t("exports.error.onlyForms"),
    );
  }

  const html = formUnifiedPollToHTML(poll, preloadedResponses);

  // Open in new window for printing
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    throw ErrorFactory.validation(
      "Popup blocked. Please allow popups for this site.",
      i18n.t("exports.error.popupBlocked"),
    );
  }

  printWindow.document.write(html);
  printWindow.document.close();
}

/**
 * Export FormUnifiedPoll to JSON
 */
export function exportFormUnifiedPollToJSON(
  poll: UnifiedPoll,
  preloadedResponses?: import("./pollStorage").FormResponse[],
): void {
  if (poll.type !== "form") {
    throw ErrorFactory.validation(
      "This function only supports FormUnifiedPoll",
      i18n.t("exports.error.onlyForms"),
    );
  }

  const responses = preloadedResponses ?? getFormResponses(poll.id);

  const exportData = {
    meta: {
      poll_id: poll.id,
      poll_slug: poll.slug,
      poll_title: poll.title,
      type: poll.type,
      created_at: poll.created_at,
      exported_at: new Date().toISOString(),
      total_responses: responses.length,
    },
    questions: poll.poll_data_json?.questions,
    responses: responses.map((r: import("./pollStorage").FormResponse) => ({
      id: r.id,
      respondent_name: r.respondentName || "Anonymous",
      created_at: r.created_at,
      items: r.items,
    })),
    conditionalRules: poll.poll_data_json?.conditionalRules || [],
  };

  const jsonString = JSON.stringify(exportData, null, 2);
  const filename = generateFilename(poll, "json");
  const blob = new Blob([jsonString], {
    type: "application/json;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Legacy aliases for backward compatibility
export const exportFormPollToCSV = exportFormUnifiedPollToCSV;
export const exportFormPollToPDF = exportFormUnifiedPollToPDF;
export const exportFormPollToJSON = exportFormUnifiedPollToJSON;
export const exportFormPollToMarkdown = exportFormUnifiedPollToMarkdown;
export const exportDatePollToCSV = exportDateUnifiedPollToCSV;

/**
 * Export FormUnifiedPoll to Markdown
 */
export function exportFormUnifiedPollToMarkdown(
  poll: UnifiedPoll,
  preloadedResponses?: import("./pollStorage").FormResponse[],
): void {
  if (poll.type !== "form") {
    throw ErrorFactory.validation(
      "This function only supports FormUnifiedPoll",
      i18n.t("exports.error.onlyForms"),
    );
  }

  const responses = preloadedResponses ?? getFormResponses(poll.id);
  const exportDate = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  let markdown = `# ${poll.title}\n\n`;
  markdown += `**${i18n.t("exports.type")}:** ${i18n.t("exports.questionnaire")}  \n`;
  markdown += `**${i18n.t("exports.responses")}:** ${responses.length}  \n`;
  markdown += `**${i18n.t("exports.questions")}:** ${poll.poll_data_json?.questions?.length || 0}  \n`;
  markdown += `**${i18n.t("exports.exportedOn")}:** ${exportDate}  \n`;
  markdown += `**ID:** ${poll.id}  \n`;
  markdown += `**Slug:** ${poll.slug}\n\n`;
  markdown += `---\n\n`;

  // Questions
  poll.poll_data_json?.questions?.forEach(
    (question: import("./pollStorage").FormQuestionShape, idx: number) => {
      markdown += `## ${i18n.t("exports.question")} ${idx + 1}: ${question.title}\n\n`;
      markdown += `**${i18n.t("exports.type")}:** ${question.type}`;
      if (question.required) markdown += ` * **${i18n.t("exports.required")}**`;
      markdown += `\n\n`;

      const questionResponses = responses.flatMap(
        (r: import("./pollStorage").FormResponse) =>
          r.items.filter(
            (item: { questionId: string }) => item.questionId === question.id,
          ),
      );

      if (question.type === "text") {
        markdown += `### ${i18n.t("exports.textResponses")}\n\n`;
        questionResponses.forEach((item: { value: unknown }, i: number) => {
          markdown += `${i + 1}. ${String(item.value)}\n`;
        });
        markdown += `\n`;
      } else if (question.type === "matrix") {
        // Matrix: display as table
        const matrixRows = question.matrixRows || [];
        const matrixCols = question.matrixColumns || [];

        // Count responses per cell (rowId_colId)
        const cellCounts: Record<string, number> = {};
        questionResponses.forEach((item: { value: unknown }) => {
          if (
            typeof item.value === "object" &&
            item.value !== null &&
            !Array.isArray(item.value)
          ) {
            const matrixVal = item.value as Record<string, string | string[]>;
            Object.entries(matrixVal).forEach(
              ([rowId, colValue]: [string, string | string[]]) => {
                const colIds = Array.isArray(colValue) ? colValue : [colValue];
                colIds.forEach((colId: string) => {
                  const key = `${rowId}_${colId}`;
                  cellCounts[key] = (cellCounts[key] || 0) + 1;
                });
              },
            );
          }
        });

        markdown += `### ${i18n.t("exports.results")} (${i18n.t("exports.responseCount", { count: responses.length })})\n\n`;
        markdown += `| | ${matrixCols.map((c: { label: string }) => c.label).join(" | ")} |\n`;
        markdown += `|${matrixCols.map((_col: { id: string; label: string }) => "---").join("|")}---|\n`;

        matrixRows.forEach((row: { id: string; label: string }) => {
          const rowLabel = row.label;
          const counts = matrixCols.map((col: { id: string }) => {
            const key = `${row.id}_${col.id}`;
            const count = cellCounts[key] || 0;
            const pct =
              responses.length > 0
                ? ((count / responses.length) * 100).toFixed(1)
                : "0.0";
            return `${count} (${pct}%)`;
          });
          markdown += `| ${rowLabel} | ${counts.join(" | ")} |\n`;
        });
        markdown += `\n`;
      } else {
        // Count options
        const optionCounts: Record<string, number> = {};
        questionResponses.forEach((item: { value: unknown }) => {
          if (Array.isArray(item.value)) {
            (item.value as Array<string>).forEach((v: string) => {
              optionCounts[v] = (optionCounts[v] || 0) + 1;
            });
          } else {
            optionCounts[String(item.value)] =
              (optionCounts[String(item.value)] || 0) + 1;
          }
        });

        markdown += `### ${i18n.t("exports.results")}\n\n`;
        markdown += `| ${i18n.t("exports.option")} | ${i18n.t("exports.responses")} | ${i18n.t("exports.percentage")} |\n`;
        markdown += `|--------|----------|-------------|\n`;

        Object.entries(optionCounts).forEach(
          ([option, count]: [string, number]) => {
            const percentage =
              responses.length > 0
                ? ((count / responses.length) * 100).toFixed(1)
                : "0.0";
            markdown += `| ${option} | ${count} | ${percentage}% |\n`;
          },
        );

        markdown += `\n`;
      }
    },
  );

  markdown += `---\n\n`;
  markdown += `*${i18n.t("exports.generatedByOn", { date: exportDate })}*\n`;

  const filename = generateFilename(poll, "md");
  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
